import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/api/auth';
import { portalMode } from '@/config/portal';
import { getApiErrorMessage } from '@/utils/apiError';
import { storage } from '@/utils/storage';

export function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authApi.adminLogin({ email, password });
      storage.setAuthMethod('password');
      login(response.tokenString, response.refreshToken, {
        id: response.user.id,
        userId: response.user.id,
        email: response.user.email,
        tenantId: response.user.tenantId,
        fullName: response.user.fullName,
        mobileNo: response.user.mobileNo,
      });
      navigate('/');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, t('auth.login.loginFailed')));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold">{t('auth.login.adminLogin')}</h2>
        <p className="mt-2 text-muted-foreground">{t('auth.login.adminLoginSubtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div role="alert" className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm text-center">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium">
            {t('auth.login.emailLabel')}
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder={t('auth.login.emailPlaceholder')}
            dir="ltr"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium">
            {t('auth.login.passwordLabel')}
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder={t('auth.login.passwordPlaceholder')}
            dir="ltr"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {isLoading ? t('auth.login.loginLoading') : t('auth.login.loginButton')}
        </button>

        <div className="text-center space-y-2">
          <div>
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">
              {t('auth.login.forgotPassword')}
            </Link>
          </div>
          {portalMode !== 'superadmin' && <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">
            {t('auth.login.backToPhoneLogin')}
          </Link>}
        </div>
      </form>
    </div>
  );
}
